import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Plus, Edit2, Trash2, ToggleLeft, ToggleRight, Save, X,
  Loader2, Box, MapPin, DollarSign, Shield, ChevronDown, ChevronUp, AlertCircle
} from "lucide-react";
import { Card, GoldButton, inputClassLight as inputClass, labelClassLight as labelClass } from "./shared/UIComponents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOAN_TYPE_LABELS = {
  fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi",
  construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial",
};

const PROPERTY_TYPE_LABELS = {
  single_family: "Single Family", multi_family: "Multi-Family", condo: "Condo", townhouse: "Townhouse",
  mixed_use: "Mixed Use", commercial: "Commercial", industrial: "Industrial", land: "Land",
};

const EMPTY_FORM = {
  name: "", loan_types: [], states: [], property_types: [],
  min_fico: "", max_fico: "", min_ltv: "", max_ltv: "", min_ltc: "", max_ltc: "",
  min_loan_amount: "", max_loan_amount: "", min_dscr: "", min_experience: "", max_closing_days: "",
  active: true,
};

export default function LoanBoxManager() {
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState({ loan_types: [], states: [], property_types: [] });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [expandedBox, setExpandedBox] = useState(null);

  const fetchBoxes = useCallback(() => {
    axios.get(`${API}/loan-box`).then(r => setBoxes(r.data.boxes || [])).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchBoxes();
    axios.get(`${API}/loan-box/options/metadata`).then(r => setOptions(r.data)).catch(console.error);
  }, [fetchBoxes]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM });
    setEditingId(null);
    setError(null);
    setShowForm(true);
  };

  const openEdit = (box) => {
    setForm({
      name: box.name || "",
      loan_types: box.loan_types || [],
      states: box.states || [],
      property_types: box.property_types || [],
      min_fico: box.min_fico ?? "",
      max_fico: box.max_fico ?? "",
      min_ltv: box.min_ltv ?? "",
      max_ltv: box.max_ltv ?? "",
      min_ltc: box.min_ltc ?? "",
      max_ltc: box.max_ltc ?? "",
      min_loan_amount: box.min_loan_amount ?? "",
      max_loan_amount: box.max_loan_amount ?? "",
      min_dscr: box.min_dscr ?? "",
      min_experience: box.min_experience ?? "",
      max_closing_days: box.max_closing_days ?? "",
      active: box.active ?? true,
    });
    setEditingId(box.box_id);
    setError(null);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name,
        loan_types: form.loan_types,
        states: form.states,
        property_types: form.property_types,
        active: form.active,
      };
      if (form.min_fico !== "") payload.min_fico = parseInt(form.min_fico);
      if (form.max_fico !== "") payload.max_fico = parseInt(form.max_fico);
      if (form.min_ltv !== "") payload.min_ltv = parseFloat(form.min_ltv);
      if (form.max_ltv !== "") payload.max_ltv = parseFloat(form.max_ltv);
      if (form.min_ltc !== "") payload.min_ltc = parseFloat(form.min_ltc);
      if (form.max_ltc !== "") payload.max_ltc = parseFloat(form.max_ltc);
      if (form.min_loan_amount !== "") payload.min_loan_amount = parseFloat(form.min_loan_amount);
      if (form.max_loan_amount !== "") payload.max_loan_amount = parseFloat(form.max_loan_amount);
      if (form.min_dscr !== "") payload.min_dscr = parseFloat(form.min_dscr);
      if (form.min_experience !== "") payload.min_experience = parseInt(form.min_experience);
      if (form.max_closing_days !== "") payload.max_closing_days = parseInt(form.max_closing_days);

      if (editingId) {
        await axios.put(`${API}/loan-box/${editingId}`, payload);
      } else {
        await axios.post(`${API}/loan-box`, payload);
      }
      setShowForm(false);
      fetchBoxes();
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const handleDelete = async (boxId) => {
    if (!window.confirm("Delete this loan box?")) return;
    try {
      await axios.delete(`${API}/loan-box/${boxId}`);
      fetchBoxes();
    } catch (err) { console.error(err); }
  };

  const handleToggle = async (boxId) => {
    try {
      await axios.patch(`${API}/loan-box/${boxId}/toggle`);
      fetchBoxes();
    } catch (err) { console.error(err); }
  };

  const toggleMultiSelect = (field, value) => {
    setForm(f => ({
      ...f,
      [field]: f[field].includes(value) ? f[field].filter(v => v !== value) : [...f[field], value],
    }));
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;

  return (
    <div data-testid="loan-box-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-[#0A0A0A]">My Loan Boxes</h2>
          <p className="text-sm text-[#666] mt-1">Define your lending criteria to automatically receive matching deals.</p>
        </div>
        <GoldButton onClick={openCreate} className="!py-3 !px-6 text-xs" data-testid="create-loan-box-btn">
          <Plus className="h-4 w-4 inline mr-2" />New Loan Box
        </GoldButton>
      </div>

      {/* Boxes List */}
      {boxes.length === 0 ? (
        <Card hover={false}>
          <div className="text-center py-12">
            <Box className="h-12 w-12 text-emerald-200 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-[#0A0A0A] mb-2">No Loan Boxes Yet</h3>
            <p className="text-sm text-[#666] mb-6">Create a loan box to define your lending criteria and start receiving matched deals automatically.</p>
            <GoldButton onClick={openCreate} className="!py-3 text-xs" data-testid="create-first-box-btn">
              <Plus className="h-4 w-4 inline mr-2" />Create Your First Loan Box
            </GoldButton>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {boxes.map((box, i) => (
            <motion.div key={box.box_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <div className={`rounded-2xl border ${box.active ? "border-emerald-200 bg-white" : "border-cream-warm bg-cream/50 opacity-70"} shadow-sm overflow-hidden`}
                data-testid={`loan-box-${box.box_id}`}>
                {/* Box Header */}
                <div className="p-5 flex items-center justify-between cursor-pointer" onClick={() => setExpandedBox(expandedBox === box.box_id ? null : box.box_id)}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${box.active ? "bg-emerald-100 border border-emerald-200" : "bg-gray-100 border border-gray-200"}`}>
                      <Box className={`h-5 w-5 ${box.active ? "text-emerald-700" : "text-gray-400"}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-[#0A0A0A]">{box.name}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${box.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                          {box.active ? "Active" : "Paused"}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-[#888]">
                        {box.loan_types?.length > 0 && <span>{box.loan_types.map(t => LOAN_TYPE_LABELS[t] || t).join(", ")}</span>}
                        {box.states?.length > 0 && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{box.states.length} states</span>}
                        {box.deals_matched > 0 && <span>{box.deals_matched} deals matched</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={(e) => { e.stopPropagation(); handleToggle(box.box_id); }} className="p-2 hover:bg-cream rounded-lg transition-colors" data-testid={`toggle-box-${box.box_id}`}>
                      {box.active ? <ToggleRight className="h-5 w-5 text-emerald-600" /> : <ToggleLeft className="h-5 w-5 text-gray-400" />}
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); openEdit(box); }} className="p-2 hover:bg-cream rounded-lg transition-colors" data-testid={`edit-box-${box.box_id}`}>
                      <Edit2 className="h-4 w-4 text-[#666]" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(box.box_id); }} className="p-2 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-box-${box.box_id}`}>
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </button>
                    {expandedBox === box.box_id ? <ChevronUp className="h-4 w-4 text-[#888]" /> : <ChevronDown className="h-4 w-4 text-[#888]" />}
                  </div>
                </div>

                {/* Expanded Details */}
                <AnimatePresence>
                  {expandedBox === box.box_id && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                      <div className="px-5 pb-5 border-t border-cream-warm pt-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          {box.min_fico && <div><span className="text-[#999]">Min FICO:</span> <span className="font-medium text-[#0A0A0A] ml-1">{box.min_fico}</span></div>}
                          {box.max_fico && <div><span className="text-[#999]">Max FICO:</span> <span className="font-medium text-[#0A0A0A] ml-1">{box.max_fico}</span></div>}
                          {box.max_ltv && <div><span className="text-[#999]">Max LTV:</span> <span className="font-medium text-[#0A0A0A] ml-1">{(box.max_ltv * 100).toFixed(0)}%</span></div>}
                          {box.max_ltc && <div><span className="text-[#999]">Max LTC:</span> <span className="font-medium text-[#0A0A0A] ml-1">{(box.max_ltc * 100).toFixed(0)}%</span></div>}
                          {box.min_loan_amount && <div><span className="text-[#999]">Min Loan:</span> <span className="font-medium text-[#0A0A0A] ml-1">${box.min_loan_amount.toLocaleString()}</span></div>}
                          {box.max_loan_amount && <div><span className="text-[#999]">Max Loan:</span> <span className="font-medium text-[#0A0A0A] ml-1">${box.max_loan_amount.toLocaleString()}</span></div>}
                          {box.min_dscr && <div><span className="text-[#999]">Min DSCR:</span> <span className="font-medium text-[#0A0A0A] ml-1">{box.min_dscr}</span></div>}
                          {box.min_experience != null && <div><span className="text-[#999]">Min Exp:</span> <span className="font-medium text-[#0A0A0A] ml-1">{box.min_experience} projects</span></div>}
                          {box.max_closing_days && <div><span className="text-[#999]">Max Close:</span> <span className="font-medium text-[#0A0A0A] ml-1">{box.max_closing_days} days</span></div>}
                        </div>
                        {box.property_types?.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1.5">
                            {box.property_types.map(t => (
                              <span key={t} className="px-2 py-0.5 rounded-md text-[10px] bg-blue-50 text-blue-700 border border-blue-100">{PROPERTY_TYPE_LABELS[t] || t}</span>
                            ))}
                          </div>
                        )}
                        {box.states?.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1.5">
                            {box.states.map(s => (
                              <span key={s} className="px-2 py-0.5 rounded-md text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-100">{s}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setShowForm(false)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={e => e.stopPropagation()} data-testid="loan-box-form-modal"
              className="relative w-full max-w-2xl rounded-2xl border border-cream-warm bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
              <button onClick={() => setShowForm(false)} className="absolute top-4 right-4 p-2 text-[#999] hover:text-[#0A0A0A]"><X className="h-5 w-5" /></button>
              <h3 className="text-lg font-bold text-[#0A0A0A] mb-1 flex items-center gap-2">
                <Box className="h-5 w-5 text-emerald-700" />{editingId ? "Edit Loan Box" : "Create Loan Box"}
              </h3>
              <p className="text-xs text-[#888] mb-6">Define your lending criteria. Deals matching these criteria will appear in your marketplace.</p>

              <form onSubmit={handleSave} className="space-y-5">
                {/* Name */}
                <label className="block">
                  <span className={labelClass}>Box Name</span>
                  <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className={inputClass} placeholder='e.g., "Fix & Flip Texas"' data-testid="lb-name" />
                </label>

                {/* Loan Types */}
                <div>
                  <span className={labelClass}>Loan Types</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {options.loan_types.map(t => (
                      <button key={t} type="button" onClick={() => toggleMultiSelect("loan_types", t)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${form.loan_types.includes(t) ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-cream border-cream-warm text-[#666] hover:border-emerald-200"}`}
                        data-testid={`lb-type-${t}`}>
                        {LOAN_TYPE_LABELS[t]}
                      </button>
                    ))}
                  </div>
                  {form.loan_types.length === 0 && <p className="text-[10px] text-[#999] mt-1">Leave empty to accept all loan types</p>}
                </div>

                {/* States */}
                <div>
                  <span className={labelClass}>States</span>
                  <div className="flex flex-wrap gap-1.5 mt-1 max-h-32 overflow-y-auto p-2 rounded-xl border border-cream-warm bg-cream/30">
                    {options.states.map(s => (
                      <button key={s} type="button" onClick={() => toggleMultiSelect("states", s)}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${form.states.includes(s) ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-white border-cream-warm text-[#666] hover:border-emerald-200"}`}
                        data-testid={`lb-state-${s}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                  {form.states.length > 0 && <p className="text-[10px] text-emerald-600 mt-1">{form.states.length} states selected</p>}
                  {form.states.length === 0 && <p className="text-[10px] text-[#999] mt-1">Leave empty for national coverage</p>}
                </div>

                {/* Property Types */}
                <div>
                  <span className={labelClass}>Property Types</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {options.property_types.map(t => (
                      <button key={t} type="button" onClick={() => toggleMultiSelect("property_types", t)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${form.property_types.includes(t) ? "bg-blue-100 border-blue-300 text-blue-800" : "bg-cream border-cream-warm text-[#666] hover:border-blue-200"}`}
                        data-testid={`lb-prop-${t}`}>
                        {PROPERTY_TYPE_LABELS[t]}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Financial Criteria */}
                <div>
                  <span className={labelClass}>Financial Criteria</span>
                  <div className="grid grid-cols-2 gap-3 mt-1">
                    <label className="block">
                      <span className="text-xs text-[#888]">Min FICO</span>
                      <input type="number" min="300" max="850" value={form.min_fico} onChange={e => setForm(f => ({ ...f, min_fico: e.target.value }))}
                        className={inputClass + " !py-2.5 text-sm"} placeholder="e.g., 650" data-testid="lb-min-fico" />
                    </label>
                    <label className="block">
                      <span className="text-xs text-[#888]">Max FICO</span>
                      <input type="number" min="300" max="850" value={form.max_fico} onChange={e => setForm(f => ({ ...f, max_fico: e.target.value }))}
                        className={inputClass + " !py-2.5 text-sm"} placeholder="e.g., 850" data-testid="lb-max-fico" />
                    </label>
                    <label className="block">
                      <span className="text-xs text-[#888]">Max LTV (decimal, e.g., 0.75)</span>
                      <input type="number" step="0.01" min="0" max="1.5" value={form.max_ltv} onChange={e => setForm(f => ({ ...f, max_ltv: e.target.value }))}
                        className={inputClass + " !py-2.5 text-sm"} placeholder="e.g., 0.75" data-testid="lb-max-ltv" />
                    </label>
                    <label className="block">
                      <span className="text-xs text-[#888]">Max LTC (decimal, e.g., 0.90)</span>
                      <input type="number" step="0.01" min="0" max="1.5" value={form.max_ltc} onChange={e => setForm(f => ({ ...f, max_ltc: e.target.value }))}
                        className={inputClass + " !py-2.5 text-sm"} placeholder="e.g., 0.90" data-testid="lb-max-ltc" />
                    </label>
                    <label className="block">
                      <span className="text-xs text-[#888]">Min Loan Amount ($)</span>
                      <input type="number" min="0" value={form.min_loan_amount} onChange={e => setForm(f => ({ ...f, min_loan_amount: e.target.value }))}
                        className={inputClass + " !py-2.5 text-sm"} placeholder="e.g., 100000" data-testid="lb-min-loan" />
                    </label>
                    <label className="block">
                      <span className="text-xs text-[#888]">Max Loan Amount ($)</span>
                      <input type="number" min="0" value={form.max_loan_amount} onChange={e => setForm(f => ({ ...f, max_loan_amount: e.target.value }))}
                        className={inputClass + " !py-2.5 text-sm"} placeholder="e.g., 2000000" data-testid="lb-max-loan" />
                    </label>
                    <label className="block">
                      <span className="text-xs text-[#888]">Min DSCR</span>
                      <input type="number" step="0.01" min="0" value={form.min_dscr} onChange={e => setForm(f => ({ ...f, min_dscr: e.target.value }))}
                        className={inputClass + " !py-2.5 text-sm"} placeholder="e.g., 1.05" data-testid="lb-min-dscr" />
                    </label>
                    <label className="block">
                      <span className="text-xs text-[#888]">Min Experience (projects)</span>
                      <input type="number" min="0" value={form.min_experience} onChange={e => setForm(f => ({ ...f, min_experience: e.target.value }))}
                        className={inputClass + " !py-2.5 text-sm"} placeholder="e.g., 3" data-testid="lb-min-exp" />
                    </label>
                  </div>
                </div>

                {/* Closing */}
                <label className="block">
                  <span className={labelClass}>Max Closing Days</span>
                  <input type="number" min="1" value={form.max_closing_days} onChange={e => setForm(f => ({ ...f, max_closing_days: e.target.value }))}
                    className={inputClass} placeholder="e.g., 14" data-testid="lb-max-close" />
                </label>

                {error && (
                  <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="lb-error">
                    <AlertCircle className="h-4 w-4 shrink-0" />{error}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <GoldButton type="submit" disabled={saving} data-testid="lb-save-btn">
                    {saving ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Saving...</span> : <span className="flex items-center gap-2"><Save className="h-4 w-4" />{editingId ? "Update" : "Create"} Loan Box</span>}
                  </GoldButton>
                  <GoldButton type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</GoldButton>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
